import React, { useCallback, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { styled } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";

import RequieredSiteFields, {
  ConfigurationItemName
} from "./RequieredSiteFields";
import { ModalMainContent } from "../duplicate/DuplicateSiteModal";
import { getInitialConfigurationValue } from "../../lib/siteHelpers";
import SiteOperationModalActionButtons from "./SiteOperationModalActionButtons";
import {
  getHasRequiredSiteOperationData,
  getSiteOperationTempData
} from "../../redux/selectors/siteSelectors";
import { delay, isURL } from "../../lib/helpers";
import { setInitialSiteOperationDetails } from "../site/siteSlice";
import { AdditionalRefMethods } from "../addNewConfiguration/TemporaryNewConfigurationData";

const ClientDetailsContainer = styled(Stack)(({ theme }) => ({
  flexDirection: "column",
  gap: theme.spacing(3),
  alignItems: "center",
  color: theme.palette.secondary.main,
  justifyContent: "space-evenly"
}));
const SiteSectionName = styled(Typography)(({ theme }) => ({
  fontSize: "1.25rem",
  fontWeight: "500",
  color: theme.palette.text.primary,
  alignSelf: "flex-start",
  padding: "15px 0px 10px 0px"
}));
const ConfigurationItemContainer = styled(Stack)(({ theme }) => ({
  alignItems: "center",
  flexDirection: "column",
  gap: theme.spacing(3)
}));

const ConfigurationItemsContainer = styled(Box)(() => ({
  padding: "20px",
  overflowY: "scroll",
  "&::-webkit-scrollbar": {
    display: "none"
  },
  scrollbarWidth: "none",
  msOverflowStyle: "none",
  width: "100%"
}));

type RequiredConfigurationsItemsProps = {
  siteOperationData: SiteConfigurationOperationResponse;
  handleModalClose: () => void;
  clientName?: string;
  submitBtnName: string;
  onSubmitRequiredFieldChanges: (
    pendingChanges: PendingChangeType[],
    addionalNewSiteInfo: SiteConfigurationOperationResponse
  ) => void;
  disableActionButtons?: boolean;
  additionaParentRef?: React.RefObject<AdditionalRefMethods>;
  onRegister?: (value: AdditionalRefMethods) => void;
  onUnRegister?: (additionalPropertyToChange?: {
    propertyName: string;
    value: string | number | boolean | null;
  }) => void;
  updateAdditionRefMethods?: (
    method: string,
    value: string | number | boolean | object | null
  ) => void;
  onMounted?: () => void;
};

export type DuplicateSiteRequiredType = ConfigurationItemType & {
  currentValue: string | string[] | boolean | number | null;
  currentValuesPerEnv: {
    [envId in string]: string | string[] | boolean | number | null;
  };
};
export type RequiredSiteFieldsValidationErrors = {
  [configItemId in string]: {
    currentValueErrorMessage: string;
    currentValuesPerEnvValidationErrors: {
      [envId in string]: string;
    };
  };
};

const RequiredConfigurationsItems = (
  props: RequiredConfigurationsItemsProps
) => {
  const {
    clientName,
    siteOperationData,
    handleModalClose,
    disableActionButtons = false,
    additionaParentRef,
    onRegister,
    onUnRegister,
    onMounted
  } = props;

  const [requiredSiteFields, setRequireSiteFields] = React.useState<{
    [configItemId in string]: DuplicateSiteRequiredType;
  }>({});
  const [validationErrors, setValidationErrors] =
    React.useState<RequiredSiteFieldsValidationErrors>({});

  const hasRequiredSiteOperationData = useSelector(
    getHasRequiredSiteOperationData
  );
  const dispatch = useDispatch();
  const hasRequiredSiteOperationDataRef = useRef(hasRequiredSiteOperationData);

  const siteOperationTempData = useSelector(getSiteOperationTempData);
  const selectedEnvironmentsList =
    siteOperationTempData.selectedEnvironmentsList;

  const requiredSiteConfigurationItems =
    siteOperationData?.requiredSiteConfigurationItems || [];
  const allConfigurationsMenuType =
    siteOperationData?.siteConfigurationsByMenuType.allConfigurationsMenuTypes;

  const getCurrentRequiredFields = useCallback(() => {
    return requiredSiteConfigurationItems.reduce<{
      [configItemId in string]: DuplicateSiteRequiredType;
    }>((acc, configurationItem: ConfigurationItemType) => {
      const { dataType, configurationItemId, validationRules } =
        configurationItem;

      const currentValuesPerEnv = selectedEnvironmentsList.reduce<{
        [configItemId in string]: ConfigurationValueInputType;
      }>((acc, currentEnvId: string) => {
        const currentValidationRule =
          validationRules.find((el) => el.environmentId === currentEnvId)
            ?.validationRules ||
          validationRules[0].validationRules ||
          null;

        const initialFieldValue =
          getInitialConfigurationValue(dataType, currentValidationRule) || "";
        return { ...acc, [currentEnvId]: initialFieldValue };
      }, {});

      return {
        ...acc,
        [configurationItemId]: {
          ...configurationItem,
          currentValue:
            getInitialConfigurationValue(
              dataType,
              // use first value since we assume we have same env validtaion rules for all envs
              validationRules[0].validationRules
            ) || "",
          currentValuesPerEnv
        }
      };
    }, {});
  }, [requiredSiteConfigurationItems, selectedEnvironmentsList]);

  const getEmptyValidationErrors = useCallback(
    () =>
      requiredSiteConfigurationItems.reduce<RequiredSiteFieldsValidationErrors>(
        (acc, el) => {
          const currentValuesPerEnvValidationErrors =
            selectedEnvironmentsList.reduce<{
              [configItemId in string]: string;
            }>((acc, currentEnvId) => {
              // eslint-disable-next-line no-param-reassign
              acc[currentEnvId] = "";
              return acc;
            }, {});

          // eslint-disable-next-line no-param-reassign
          acc[el.configurationItemId] = {
            currentValueErrorMessage: "",
            currentValuesPerEnvValidationErrors
          };
          return acc;
        },
        {}
      ),
    [requiredSiteConfigurationItems, selectedEnvironmentsList]
  );

  const updateConfigItemCurrentValue = useCallback(
    (
      configurationItemId: string,
      currentValue: string | string[] | boolean | number | null,
      currentValueEnvId?: string
    ) => {
      const { uniquePerEnvironment } = requiredSiteFields[configurationItemId];
      if (
        uniquePerEnvironment &&
        currentValueEnvId &&
        !!requiredSiteFields[configurationItemId].currentValuesPerEnv
      ) {
        setRequireSiteFields((prevValue) => ({
          ...prevValue,
          [configurationItemId]: {
            ...prevValue[configurationItemId],
            currentValuesPerEnv: {
              ...prevValue[configurationItemId].currentValuesPerEnv,
              [currentValueEnvId]: currentValue
            }
          }
        }));
      } else {
        setRequireSiteFields((prevValue) => ({
          ...prevValue,
          [configurationItemId]: {
            ...prevValue[configurationItemId],
            currentValue
          }
        }));
      }
    },
    [requiredSiteFields, setRequireSiteFields]
  );
  const updateValidationErrors = useCallback(
    (
      errorMessage: string,
      configItemId: string,
      uniquePerEnvironment?: boolean,
      currentValueEnvId?: string
    ) => {
      setValidationErrors((prevValue) => ({
        ...prevValue,
        [configItemId]: {
          currentValueErrorMessage: uniquePerEnvironment ? "" : errorMessage,
          currentValuesPerEnvValidationErrors:
            uniquePerEnvironment && currentValueEnvId
              ? {
                  ...prevValue[configItemId]
                    .currentValuesPerEnvValidationErrors,
                  [currentValueEnvId]: errorMessage
                }
              : {
                  ...prevValue[configItemId].currentValuesPerEnvValidationErrors
                }
        }
      }));
    },
    [setValidationErrors]
  );
  const validateRequiredFields = useCallback(() => {
    let hasValidationErrors = false;
    const errorList: { errorMessage: string; id: string }[] = [];
    Object.keys(requiredSiteFields).forEach((configItemId: string) => {
      const {
        currentValue,
        uniquePerEnvironment,
        currentValuesPerEnv,
        validationRules,
        dataType
      } = requiredSiteFields[configItemId];

      if (!uniquePerEnvironment) {
        if (validationRules[0].isRequired && !currentValue) {
          updateValidationErrors("No empty values allowed.", configItemId);
          errorList.push({
            id: configItemId,
            errorMessage: "No empty values allowed."
          });
          hasValidationErrors = true;
        } else if (dataType === "url" && typeof currentValue === "string") {
          if (!isURL(currentValue)) {
            updateValidationErrors("Must be a valid URL value.", configItemId);
            hasValidationErrors = true;
            errorList.push({
              id: configItemId,
              errorMessage: "Must be a valid URL value."
            });
          }
        }
      } else if (uniquePerEnvironment && currentValuesPerEnv) {
        Object.keys(currentValuesPerEnv).forEach((envId, i, envArr) => {
          const currentSingleValuePerEnv = currentValuesPerEnv[envId];
          const currentValidationRule = validationRules.find(
            (el) => el.environmentId === envId
          );
          const nextSingleValuePerEnv =
            i < envArr.length ? currentValuesPerEnv[envArr[i + 1]] : null;

          if (!currentSingleValuePerEnv && currentValidationRule?.isRequired) {
            updateValidationErrors(
              "No empty values allowed.",
              configItemId,
              uniquePerEnvironment,
              envId
            );
            errorList.push({
              id: configItemId,
              errorMessage: "No empty values allowed."
            });
            hasValidationErrors = true;
            return;
          }
          if (
            dataType === "url" &&
            typeof currentSingleValuePerEnv === "string" &&
            currentSingleValuePerEnv
          ) {
            if (!isURL(currentSingleValuePerEnv)) {
              updateValidationErrors(
                "Must be a valid URL value.",
                configItemId,
                uniquePerEnvironment,
                envId
              );
              hasValidationErrors = true;
              errorList.push({
                id: configItemId,
                errorMessage: "Must be a valid URL value."
              });
              return;
            }
          }
          if (
            currentSingleValuePerEnv &&
            nextSingleValuePerEnv &&
            typeof currentSingleValuePerEnv === "string" &&
            typeof nextSingleValuePerEnv === "string" &&
            currentSingleValuePerEnv?.trim() === nextSingleValuePerEnv?.trim()
          ) {
            updateValidationErrors(
              "URL values must be unique per environment.",
              configItemId,
              uniquePerEnvironment,
              envId
            );
            updateValidationErrors(
              "URL values must be unique per environment.",
              configItemId,
              uniquePerEnvironment,
              envArr[i + 1]
            );
            errorList.push({
              id: configItemId,
              errorMessage: "URL values must be unique per environment."
            });
            hasValidationErrors = true;
            return;
          }
        });
      }
    });
    if (hasValidationErrors)
      return {
        hasValidationErrors,
        errorList
      };

    return {
      hasValidationErrors,
      errorList
    };
  }, [requiredSiteFields, validationErrors, updateValidationErrors, isURL]);

  useEffect(() => {
    if (!requiredSiteConfigurationItems?.length) return;
    const currentRequiredFields = getCurrentRequiredFields();
    const emptyValidationErrors = getEmptyValidationErrors();

    setValidationErrors(emptyValidationErrors);
    setRequireSiteFields(currentRequiredFields);
  }, [
    requiredSiteConfigurationItems,
    selectedEnvironmentsList,
    setValidationErrors,
    setRequireSiteFields,
    getCurrentRequiredFields,
    getEmptyValidationErrors
  ]);

  useEffect(() => {
    hasRequiredSiteOperationDataRef.current = hasRequiredSiteOperationData;
  }, [hasRequiredSiteOperationData]);

  useEffect(() => {
    return () => {
      if (
        !hasRequiredSiteOperationDataRef.current &&
        siteOperationTempData.mode !== "newSiteConfiguration"
      ) {
        dispatch(
          setInitialSiteOperationDetails({
            mode: null,
            selectedEnvironmentsForOperation: []
          })
        );
      }
    };
  }, []);

  useEffect(() => {
    if (props.updateAdditionRefMethods) {
      props.updateAdditionRefMethods("validationErrors", validationErrors);
    }
  }, [validationErrors]);

  useEffect(() => {
    if (!onRegister || !onUnRegister) return;

    if (
      additionaParentRef?.current !== undefined &&
      Object.values(requiredSiteFields)?.length > 0
    ) {
      onRegister({
        getData: () => requiredSiteFields,
        validateCurrentData: async () => {
          const errorDetails = validateRequiredFields();
          await delay(20);
          return {
            hasErrors: errorDetails?.hasValidationErrors || false,
            errorList: errorDetails?.errorList || []
          };
        }
      });
    }
  }, [additionaParentRef, onRegister, onUnRegister, requiredSiteFields]);

  useEffect(() => {
    onMounted?.();
    const possibleConfigurationItemsInputState =
      (additionaParentRef?.current?.getData() || null) as
        | {
            [configItemId in string]: DuplicateSiteRequiredType;
          }
        | null;
    const possibleValidationErrorsState =
      additionaParentRef?.current?.validationErrors || null;

    if (possibleConfigurationItemsInputState !== null) {
      setRequireSiteFields(possibleConfigurationItemsInputState);
    }
    if (
      possibleValidationErrorsState !== null &&
      Object.values(possibleValidationErrorsState).length > 0
    ) {
      setValidationErrors(possibleValidationErrorsState);
    }

    return () => {
      if (additionaParentRef?.current && onUnRegister) {
        onUnRegister();
      }
    };
  }, []);

  const formatRequiredSiteFields = useCallback(
    (requiredSiteFields: {
      [x: string]: DuplicateSiteRequiredType;
    }): PendingChangeType[] => {
      if (
        !requiredSiteFields ||
        !Object.values(requiredSiteFields).length ||
        siteOperationTempData.mode === "newSiteConfiguration"
      )
        return [];

      return Object.values(requiredSiteFields).map<PendingChangeType>(
        (requiredField) => {
          const {
            dataType,
            configurationId,
            configurationTypeId,
            name,
            configurationItemId,
            currentValue,
            uniquePerEnvironment,
            currentValuesPerEnv,
            validationRules,
            description,
            integrationName,
            displayOrder
          } = requiredField;

          let currentConfigurationItemValues: ConfigurationValue[] = [];
          if (uniquePerEnvironment) {
            currentConfigurationItemValues = Object.keys(
              currentValuesPerEnv
            ).map((currentEnvId: string) => {
              const currentValuePerEnv = currentValuesPerEnv[currentEnvId];
              return {
                configurationValue:
                  typeof currentValuePerEnv === "string"
                    ? currentValuePerEnv.trim()
                    : currentValuePerEnv,
                environmentIds: [currentEnvId]
              };
            });
          } else {
            currentConfigurationItemValues = [
              {
                configurationValue:
                  typeof currentValue === "string"
                    ? currentValue.trim()
                    : currentValue,
                environmentIds: selectedEnvironmentsList
              }
            ];
          }

          return {
            dataType,
            sectionId: configurationId,
            sectionName:
              allConfigurationsMenuType[configurationTypeId]
                .configurationTypeName,
            configurationName: name,
            integrationName,
            description,
            configurationId,
            configurationTypeId,
            configurationItemId,
            uniquePerEnvironment,
            originalConfigurationItemValues: [],
            currentConfigurationItemValues,
            isPartOfSiteDuplicationOrCreation: true,
            validationRules,
            displayOrder
          };
        }
      );
    },
    [siteOperationTempData.mode]
  );

  const onSubmitRequiredFieldChanges = () => {
    const { hasValidationErrors } = validateRequiredFields();

    if (hasValidationErrors) return;

    const pendingChanges = formatRequiredSiteFields(requiredSiteFields);
    props.onSubmitRequiredFieldChanges(pendingChanges, siteOperationData);

    queueMicrotask(() => {
      handleModalClose();
    });
  };

  if (!requiredSiteConfigurationItems?.length) return <></>;

  return (
    <ModalMainContent
      aria-label="required configuration items section"
      sx={{
        ...(siteOperationTempData?.mode === "newSiteConfiguration" && {
          height: "98%",
          overflowY: "scroll",
          p: 0,
          m: 0,
          pb: 1
        }),
        ...(disableActionButtons && {
          gridTemplateRows: "1fr"
        })
      }}
    >
      <ConfigurationItemsContainer>
        {clientName && (
          <ClientDetailsContainer>
            <ConfigurationItemName
              sx={{ flex: 1 }}
              extraProps={{
                uniquePerEnvironment: false
              }}
            >
              Client:
            </ConfigurationItemName>
            <Box
              sx={{
                minWidth: 200,
                flex: 1
              }}
            >
              <ConfigurationItemName
                sx={{ textAlign: "left" }}
                extraProps={{
                  uniquePerEnvironment: false
                }}
                data-testid="client-name"
              >
                {clientName}
              </ConfigurationItemName>
            </Box>
          </ClientDetailsContainer>
        )}
        <Stack flexDirection={"column"} gap={3} width="100%">
          {Object.values(requiredSiteFields).map(
            (currentConfigItem, i, arr) => {
              const {
                configurationTypeId: currentConfigurationGroupingId,
                configurationItemId
              } = currentConfigItem;
              let currentSectionName = "";
              const prevConfigItem = i > 0 ? arr[i - 1] : null;

              if (
                prevConfigItem?.configurationTypeId !==
                  currentConfigItem.configurationTypeId &&
                siteOperationTempData.mode !== "newSiteConfiguration"
              ) {
                currentSectionName =
                  allConfigurationsMenuType?.[currentConfigurationGroupingId]
                    .configurationTypeName;
              }

              return (
                <ConfigurationItemContainer
                  width="100%"
                  key={`configuration-item-${currentConfigItem.configurationItemId}${i}`}
                >
                  {siteOperationTempData.mode !== "newSiteConfiguration" &&
                    currentSectionName && (
                      <SiteSectionName>{currentSectionName}</SiteSectionName>
                    )}
                  <RequieredSiteFields
                    configurationItem={currentConfigItem}
                    updateConfigItemCurrentValue={updateConfigItemCurrentValue}
                    validationErrors={validationErrors[configurationItemId]}
                    setValidationError={updateValidationErrors}
                    selectedEnvironmentsList={selectedEnvironmentsList}
                  />
                </ConfigurationItemContainer>
              );
            }
          )}
        </Stack>
      </ConfigurationItemsContainer>
      {!disableActionButtons && (
        <SiteOperationModalActionButtons
          onSubmitCb={onSubmitRequiredFieldChanges}
          onCancelCb={handleModalClose}
          submitBtnName={props.submitBtnName}
          hasSelectedEnvironmentsList
        />
      )}
    </ModalMainContent>
  );
};

export default RequiredConfigurationsItems;
